import type { BlobBackend } from './backend';

const DATABASE = 'yatwa-files';
const STORE = 'files';

/**
 * Как файл лежит в IndexedDB.
 *
 * **Не `Blob`, а байты с типом.** WebKit отказывается класть `Blob` в хранилище:
 * `Error preparing Blob/File data to be stored in object store` — поймано 04.09.2026
 * в первом же прогоне записи файла. Ошибка приходит от самой базы, обойти её нечем,
 * и она означала бы, что документ не сохранился. `ArrayBuffer` кладётся штатно,
 * а `Blob` собирается обратно при чтении — снаружи разницы нет.
 */
interface StoredBytes {
  buffer: ArrayBuffer;
  type: string;
}

/**
 * Запасное хранилище файлов — IndexedDB.
 *
 * Нужно там, где OPFS нет: iOS 16 и всё, что старше. Ключ — тот же путь, что у файлов
 * (`cases/<дело>/<документ>.pdf`), поэтому логика выше не отличает одно хранилище
 * от другого.
 */
export function idbBackend(): BlobBackend {
  let opened: Promise<IDBDatabase> | null = null;

  const open = () => {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DATABASE, 1);
      request.onupgradeneeded = () => request.result.createObjectStore(STORE);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB недоступна'));
    });
    return opened;
  };

  /**
   * Запрос к хранилищу.
   *
   * **Запись подтверждается по завершению транзакции, а не по ответу запроса.**
   * `request.onsuccess` срабатывает, когда операция принята, но транзакция ещё
   * не закоммичена: приложение уже показывало документ в списке, а закрытие вкладки
   * в эту секунду теряло и файл, и запись о нём. Поймано 05.09.2026 прогоном, где
   * сразу после добавления документа шёл переход на другой экран: файла нет,
   * документа нет, а юрист видел обоих.
   *
   * Чтение ждать нечего — оно ничего не меняет, и лишнее ожидание коммита стоило бы
   * времени на каждой строке списка.
   */
  const run = async <T>(
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> => {
    const db = await open();

    return new Promise<T>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = action(transaction.objectStore(STORE));

      const fail = (error: unknown) =>
        reject(error instanceof Error ? error : new Error('ошибка хранилища'));

      if (mode === 'readonly') {
        request.onsuccess = () => resolve(request.result);
      } else {
        transaction.oncomplete = () => resolve(request.result);
        transaction.onabort = () => fail(transaction.error);
      }

      request.onerror = () => fail(request.error);
    });
  };

  return {
    kind: 'indexeddb',

    async write(path, blob) {
      const record: StoredBytes = { buffer: await blob.arrayBuffer(), type: blob.type };
      await run('readwrite', (store) => store.put(record, path));
    },

    async read(path) {
      const record = await run<StoredBytes | undefined>('readonly', (store) => store.get(path));
      return record ? new Blob([record.buffer], { type: record.type }) : null;
    },

    async remove(path) {
      await run('readwrite', (store) => store.delete(path));
    },

    async list(prefix) {
      // Ключи и значения читаются раздельно: тянуть все файлы разом ради их размеров —
      // верный способ съесть память на деле с сотней сканов.
      const keys = await run<IDBValidKey[]>('readonly', (store) => store.getAllKeys());
      const paths = keys.map(String).filter((path) => path.startsWith(prefix));

      const found: { path: string; sizeBytes: number }[] = [];
      for (const path of paths) {
        const record = await run<StoredBytes | undefined>('readonly', (store) => store.get(path));
        if (record) found.push({ path, sizeBytes: record.buffer.byteLength });
      }
      return found;
    },
  };
}
